import cx from 'classnames'
import { MouseEventHandler, ReactNode, useEffect, useState } from 'react'
import { KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'

import { FolderIcon } from 'common/FolderIcon'
import HandbookEmojiPicker from 'components/Handbook2_0/HandbookEmojiPicker'
import HandbookIcon from 'components/Handbook2_0/HandbookIcon'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'

import Styles from './FolderBreadcrumb.module.scss'
import { useRootStore } from 'stores/RootStore'

export interface FolderBreadcrumbProps {
  title: string
  first?: boolean
  icon?: ReactNode
  to: string
  onClick?: () => void
  defaultFolder?: boolean
}

export const FolderBreadcrumbLink = ({ title, icon, to, defaultFolder }: FolderBreadcrumbProps) => {
  return (
    <>
      <Tooltip label={title} placement="bottom">
        <Link className={Styles.projectLink} to={to}>
          <div className={Styles.breadcrumb}>
            {defaultFolder ? <FolderIcon defaultFolder={!!defaultFolder} /> : icon}
            <div className={Styles.folderButtonText}>{title}</div>
          </div>
        </Link>
      </Tooltip>
    </>
  )
}

export const FolderBreadcrumb = ({ title, icon, onClick, defaultFolder }: FolderBreadcrumbProps) => {
  return (
    <>
      <Tooltip label={title} placement="bottom">
        <div className={Styles.projectLink} onClick={onClick}>
          <div className={Styles.breadcrumb}>
            {defaultFolder ? <FolderIcon defaultFolder={!!defaultFolder} /> : icon}
            <div className={Styles.folderButtonText}>{title}</div>
          </div>
        </div>
      </Tooltip>
    </>
  )
}

export const LastFolderBreadcrumb = ({
  title,
  icon,
  showDivider,
}: {
  title: string
  icon: ReactNode
  showDivider: boolean
}) => {
  return (
    <>
      {showDivider && <div className={Styles.divider}>/</div>}
      <Tooltip label={title} placement="bottom">
        <div className={Styles.currentFolder}>
          <span className={Styles.folderIcon}>{icon}</span>
          <div className={Styles.folderButtonText}>{title}</div>
        </div>
      </Tooltip>
    </>
  )
}

const INPUT_MAX_WIDTH = 200

export const CurrentFolderBreadcrumb = ({ showDivider }: { showDivider: boolean }) => {
  const { currentFolder, update, editingName, setEditingName } = useCurrentFolderStore()
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [inputRef, setInputRef] = useState<HTMLInputElement>()
  const canEdit = currentFolder?.has_edit_access
  const handbooksStore = useRootStore().handbooks

  useEffect(() => {
    if (editingName) {
      setIsEditing(true)
      inputRef?.focus()
    }
  }, [editingName, inputRef])

  useEffect(() => {
    if (!isEditing) setEditingName(false)
  }, [isEditing, setEditingName])

  useEffect(() => {
    if (!currentFolder) return

    if (inputRef) {
      inputRef.style.width = `${inputRef.value.length}ch`
      inputRef.style.maxWidth = `${Math.min(inputRef.scrollWidth, INPUT_MAX_WIDTH)}px`
    }
  }, [currentFolder, inputRef])

  const updateFolder = (params: { title?: string; emoji?: string | null }, { force }: { force?: boolean } = {}) => {
    if (!currentFolder || (!isEditing && !force)) return
    update({ ...currentFolder, ...params }, { onSuccess: () => handbooksStore.refresh(currentFolder.handbook?.key) })
    setIsEditing(false)
  }

  const onCancel = () => {
    setIsEditing(false)
    if (inputRef) inputRef.value = currentFolder?.title || ''
    setTimeout(() => inputRef?.blur(), 10)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation()

    switch (e.key) {
      case 'Enter':
        inputRef?.blur()
        break
      case 'Escape':
        onCancel()
        break
    }

    if (inputRef) {
      inputRef.style.width = `${inputRef.value.length}ch`
      inputRef.style.maxWidth = `${Math.min(inputRef.scrollWidth, INPUT_MAX_WIDTH)}px`
    }
  }

  const onEmojiPicked = (emoji: string | null) => {
    updateFolder({ emoji }, { force: true })
  }

  const onClickToEdit: MouseEventHandler = (e) => {
    e.stopPropagation()
    if (canEdit) setIsEditing(true)
  }

  const onBlur = () => {
    if (inputRef?.value.length) {
      updateFolder({ title: inputRef?.value })
    } else {
      onCancel()
    }
  }

  if (!currentFolder) return null

  return (
    <>
      {showDivider && <div className={Styles.divider}>/</div>}
      <div
        className={cx(Styles.currentFolder, { [Styles.canEdit]: canEdit, [Styles.isEditing]: isEditing })}
        onClick={onClickToEdit}
      >
        {canEdit ? (
          <>
            <HandbookEmojiPicker emoji={currentFolder?.header_emoji} folder onSelect={onEmojiPicked} />
            <Tooltip label={currentFolder?.title} placement="bottom" className={Styles.tooltip} mouseEnterDelay={1000}>
              <input
                ref={(el) => {
                  if (el) setInputRef(el)
                }}
                defaultValue={currentFolder?.title}
                placeholder="Type a name..."
                onKeyDown={onKeyDown}
                onBlur={onBlur}
              />
            </Tooltip>
          </>
        ) : (
          <>
            <span>{currentFolder?.header_emoji ?? <HandbookIcon className={Styles.defaultIcon} folder />}</span>
            <Tooltip label={currentFolder?.title} placement="bottom">
              <span className={Styles.folderButtonText}>{currentFolder?.title}</span>
            </Tooltip>
          </>
        )}
      </div>
    </>
  )
}
